import { Injectable, signal, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { LoggedInUser } from '../models/user.model';
import { Storage } from './storage.service';
import { Notification } from './notification.service';
import { RealtimeService } from './realtime.service';

interface LoginRequest {
  email: string;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class Auth {

  private apiUrl = `${environment.apiUrl}/auth`;
  private broadcastChannel: BroadcastChannel | null = null;

  /** Reactive signal so the header/sidebar can react to login and permission state instantly. */
  currentUser = signal<LoggedInUser | null>(null);

  constructor(
    private http: HttpClient,
    private storage: Storage,
    private router: Router,
    private notification: Notification,
    private ngZone: NgZone,
    private realtimeService: RealtimeService
  ) {
    this.currentUser.set(this.storage.getUser());
    this.initRealtimePermissionSync();
  }

  private initRealtimePermissionSync(): void {
    if (typeof window === 'undefined') return;

    // 1. Live SSE Server Stream Subscription
    if (this.isLoggedIn()) {
      this.realtimeService.connect();
    }
    this.realtimeService.rolePermissionsUpdated$.subscribe((event) => {
      this.syncCurrentRolePermissions(event.roleId);
    });

    // 1. Cross-Tab Broadcast Channel
    if ('BroadcastChannel' in window) {
      try {
        this.broadcastChannel = new BroadcastChannel('fixura_permissions_sync');
        this.broadcastChannel.onmessage = (event) => {
          if (event.data?.type === 'ROLE_PERMISSIONS_UPDATED') {
            this.ngZone.run(() => {
              this.syncCurrentRolePermissions(event.data.roleId);
            });
          }
        };
      } catch (e) {
        console.warn('BroadcastChannel not supported', e);
      }
    }

    // 2. Storage event listener (across browser windows/tabs)
    window.addEventListener('storage', (event) => {
      if (event.key === 'fixura_role_update_event' && event.newValue) {
        try {
          const payload = JSON.parse(event.newValue);
          this.ngZone.run(() => {
            this.syncCurrentRolePermissions(payload.roleId);
          });
        } catch (e) {}
      }
    });
  }

  /** Broadcasts a role update event to all active sessions in real time */
  broadcastRoleUpdate(roleId: number, permissions?: any[]): void {
    const payload = { type: 'ROLE_PERMISSIONS_UPDATED', roleId, timestamp: Date.now() };
    
    // Broadcast via BroadcastChannel
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(payload);
    }

    // Broadcast via Storage Event
    try {
      localStorage.setItem('fixura_role_update_event', JSON.stringify(payload));
    } catch (e) {}

    // Apply to current session if applicable
    this.syncCurrentRolePermissions(roleId, permissions);
  }

  /** Fetches latest role permissions from server and updates active user session */
  syncCurrentRolePermissions(targetRoleId?: number, directPermissions?: any[]): void {
    const user = this.storage.getUser();
    if (!user || !user.roleId) return;

    if (targetRoleId && user.roleId !== targetRoleId) {
      return; // Not this user's role
    }

    if (directPermissions && Array.isArray(directPermissions) && user.roleId === targetRoleId) {
      user.permissions = directPermissions;
      this.updateUserSession(user);
      this.enforceCurrentRouteAccess();
      this.realtimeService.notifyModuleChange('ALL', 'PERMISSIONS_UPDATED');
      return;
    }

    // Query backend for latest role state
    this.http.get<ApiResponse<any>>(`${environment.apiUrl}/roles/${user.roleId}`).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const roleData = res.data;
          const freshPermissions = roleData.modulePermissions || [];
          
          user.permissions = freshPermissions;
          user.role = roleData.roleCode || user.role;
          user.roleName = roleData.roleName || user.roleName;
          
          this.updateUserSession(user);
          this.enforceCurrentRouteAccess();
          this.realtimeService.notifyModuleChange('ALL', 'PERMISSIONS_UPDATED');
        }
      },
      error: () => {}
    });
  }

  /** Automatically redirects user away if current page permission was revoked */
  private enforceCurrentRouteAccess(): void {
    if (this.isAdmin()) return;
    const currentUrl = this.router.url;

    // Check customer routes
    if (currentUrl.startsWith('/customers') && !this.hasModuleAccess('Customers', 'view')) {
      this.router.navigate(['/dashboard']).then(() => {
        this.notification.error('Access revoked: Your Customer module permissions were updated by an administrator.');
      });
      return;
    }

    // Check project routes
    if (currentUrl.startsWith('/projects') && !this.hasModuleAccess('Projects', 'view')) {
      this.router.navigate(['/dashboard']).then(() => {
        this.notification.error('Access revoked: Your Project module permissions were updated by an administrator.');
      });
      return;
    }

    // Check staff routes
    if (currentUrl.startsWith('/staff') && !this.hasModuleAccess('Staff', 'view')) {
      this.router.navigate(['/dashboard']).then(() => {
        this.notification.error('Access revoked: Your Staff module permissions were updated by an administrator.');
      });
      return;
    }

    // Check roles routes
    if (currentUrl.startsWith('/roles') && !this.hasModuleAccess('Roles', 'view')) {
      this.router.navigate(['/dashboard']).then(() => {
        this.notification.error('Access revoked: Your Role Management permissions were updated by an administrator.');
      });
      return;
    }
  }

  login(credentials: LoginRequest): Observable<ApiResponse<LoggedInUser>> {
    return this.http.post<ApiResponse<LoggedInUser>>(`${this.apiUrl}/login`, credentials).pipe(
      tap((response) => {
        if (response.success) {
          this.storage.setUser(response.data);
          this.currentUser.set(response.data);
          this.realtimeService.connect();
        }
      })
    );
  }

  refreshToken(): Observable<ApiResponse<LoggedInUser>> {
    const refreshToken = this.storage.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<ApiResponse<LoggedInUser>>(`${this.apiUrl}/refresh`, { refreshToken }).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.storage.setUser(response.data);
          this.currentUser.set(response.data);
        }
      }),
      catchError((err) => {
        this.logout();
        return throwError(() => err);
      })
    );
  }

  logout(): void {
    this.realtimeService.disconnect();
    this.storage.clear();
    this.currentUser.set(null);
  }

  updateUserSession(user: LoggedInUser): void {
    this.storage.setUser(user);
    this.currentUser.set(user ? { ...user, permissions: Array.isArray(user.permissions) ? [...user.permissions] : [] } : null);
  }

  isLoggedIn(): boolean {
    return this.storage.isLoggedIn();
  }

  isAdmin(): boolean {
    const user = this.currentUser();
    if (!user) return false;
    
    // Explicit super admin email
    if (user.email === 'admin@gmail.com') {
      return true;
    }

    // Strict role name / role code check
    const roleCode = (user.role || this.storage.getRole() || '').toUpperCase().trim();
    const roleName = (user.roleName || '').toUpperCase().trim();

    return (
      roleCode === 'SUPER_ADMIN' ||
      roleCode === 'SUPERADMIN' ||
      roleCode === 'ADMIN' ||
      roleName === 'SUPER ADMIN' ||
      roleName === 'SUPER_ADMIN' ||
      roleName === 'ADMIN'
    );
  }

  /** Generalized role check, used by roleGuard and the sidebar to show/hide nav items. */
  hasAnyRole(roles: string[]): boolean {
    const current = this.storage.getRole();
    return current !== null && roles.includes(current);
  }

  hasModuleAccess(moduleName: string, action: 'viewGlobal' | 'view' | 'add' | 'update' | 'delete' = 'view'): boolean {
    if (this.isAdmin()) return true;
    const user = this.currentUser();
    if (!user?.permissions || !Array.isArray(user.permissions)) return false;

    const target = moduleName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalize = (value?: string) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    // An exact module match wins, so e.g. "Exit Management" never falls back
    // to the "Exit" permission through the partial matching below.
    const exact = user.permissions.find(p => normalize(p.moduleCode) === target || normalize(p.moduleName) === target);

    const perm = exact ?? user.permissions.find(p => {
      const code = (p.moduleCode || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const name = (p.moduleName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      return (
        code === target ||
        name === target ||
        (code.length > 2 && target.includes(code)) ||
        (name.length > 2 && target.includes(name)) ||
        (target.length > 2 && code.includes(target)) ||
        (target.length > 2 && name.includes(target))
      );
    });

    if (!perm) return false;
    if (action === 'view') {
      return !!perm.view || !!perm.viewGlobal;
    }
    return !!perm[action];
  }

  getUser(): LoggedInUser | null {
    return this.storage.getUser();
  }

  changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/change-password`, {
      currentPassword,
      newPassword,
      confirmPassword,
    });
  }

  setupTwoFactor(): Observable<ApiResponse<{ secretKey: string; otpAuthUrl: string; issuer: string; accountName: string }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/2fa/setup`, {});
  }

  enableTwoFactor(code: string): Observable<ApiResponse<{ status: string; message: string; backupCodes: string[] }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/2fa/enable`, { code });
  }

  disableTwoFactor(password: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/2fa/disable`, { password });
  }

  verifyTwoFactorLogin(preAuthToken: string, code: string): Observable<ApiResponse<LoggedInUser>> {
    return this.http.post<ApiResponse<LoggedInUser>>(`${this.apiUrl}/2fa/verify-login`, {
      preAuthToken,
      code,
    }).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.storage.setUser(response.data);
          this.currentUser.set(response.data);
        }
      })
    );
  }

  forgotPassword(email: string): Observable<ApiResponse<{ status: string; message: string; resetToken?: string }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/forgot-password`, { email });
  }

  resetPassword(token: string, newPassword: string, confirmPassword?: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/reset-password`, {
      token,
      newPassword,
      confirmPassword: confirmPassword || newPassword,
    });
  }
}
